/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getProductUnitsOfMeasureAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    CommerceProperty,
    ProductUnitOfMeasure,
    SimpleProduct,
    UnitOfMeasureSource
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getSelectedProductIdFromActionInput, getSelectedVariant, SelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import { updateOrAddProperty } from '../../../utilities/extension-properties-helpers';
import { getCartState } from '@msdyn365-commerce/global-state';
import { getCurrentZoneAffiliationId } from '../../../utilities/cart-helpers';
import { MTT_PRODUCT_VARIANT_ID_PROPERTY, MTT_ZONE_AFFILIATIONS_PROPERTY } from '../../../constants';

/**
 * Input class for the GetPriceForSelectedVariant Data Action.
 */
export class UomsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public zoneAffiliationId: number;

    public selectedProduct: SimpleProduct | undefined;

    public constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, zoneAffiliationId?: number) {
        this.productId = productId;
        this.selectedProduct = selectedProduct;
        this.channelId = channelId;
        this.zoneAffiliationId = zoneAffiliationId ?? 0;
    }

    public getCacheKey = (): string => 'UomsForSelectedVariantInput';

    public getCacheObjectType = (): string => 'ProductUnitOfMeasure';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForSelectedVariantDataAction.
 * @param inputData - The input data for the createInput method.
 * @returns The PriceForSelectedVariantInput.
 */
export const createUomsForSelectedVariantInput = (inputData: ICreateActionContext<IGeneric<IAny>>): UomsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new UomsForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined);
    }
    throw new Error('Unable to create UomsForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The Action Method for the GetPriceForSelectedVariant Data Action
 * Pulls the currently selected variant from the cache using the getSelectedVariant data action, and gets it's current contextual price
 * via the getActivePrice RetailServer API.
 * @param input - The input.
 * @param ctx - The ctx.
 * @returns The Promise<ProductPrice | null>.
 */
export async function getUomsForSelectedVariantAction(
    input: UomsForSelectedVariantInput,
    ctx: IActionContext
): Promise<ProductUnitOfMeasure[]> {
    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );

                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then<ProductUnitOfMeasure[]>(async (productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = productResult as SimpleProduct | undefined;
            if (activeProduct) {
                const extensionProperties: CommerceProperty[] = [];
                if (ctx.requestContext?.app?.config?.hideNotAllowedUoms === true) {
                    let zoneAffiliationId = input.zoneAffiliationId;
                    if (!zoneAffiliationId) {
                        const cartState = await getCartState(ctx);
                        zoneAffiliationId = getCurrentZoneAffiliationId(cartState?.cart) || 0;
                    }

                    updateOrAddProperty(MTT_ZONE_AFFILIATIONS_PROPERTY, { LongValue: zoneAffiliationId ?? 0 }, extensionProperties);
                    updateOrAddProperty(MTT_PRODUCT_VARIANT_ID_PROPERTY, { LongValue: activeProduct?.RecordId ?? 0 }, extensionProperties);
                }

                const productId = activeProduct?.MasterProductId ? activeProduct?.MasterProductId : activeProduct?.RecordId;
                const unitsOfMeasure = await getProductUnitsOfMeasureAsync({ callerContext: ctx }, productId, {
                    IncludeQuantityLimitRules: false,
                    ExtensionProperties: extensionProperties
                });
                const activeUoms =
                    unitsOfMeasure?.filter(
                        x =>
                            x.UnitOfMeasureSourceValue === UnitOfMeasureSource.Default ||
                            x.UnitOfMeasureSourceValue === UnitOfMeasureSource.IntraOrInterClassConversion
                    ) ?? [];
                return activeUoms;
            }

            return [];
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

export const getUomsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-uoms-for-selected-variant',
    action: getUomsForSelectedVariantAction as IAction<ProductUnitOfMeasure[]>,
    input: createUomsForSelectedVariantInput
});

export default getUomsForSelectedVariantActionDataAction;
